import {
  getData,
  getNodeList,
  isNumberNode,
  isStringNode,
  NumberNode,
  Plugin,
  StringNode,
  ValueNode,
} from '@sagold/react-json-editor';

import { relaxReadonlySchemaConstraints } from './readonlySchema';

type ReadonlyValueFormatter<T extends ValueNode> = (
  node: T
) => T['value'] | string | undefined;

export type ReadonlyFormattingPluginOptions = {
  stringFormatter: ReadonlyValueFormatter<StringNode>;
  dateFormatter: ReadonlyValueFormatter<StringNode>;
  numberFormatter: ReadonlyValueFormatter<NumberNode>;
};

export const ReadonlyFormattingPlugin: Plugin<
  ReadonlyFormattingPluginOptions
> = (he, { numberFormatter, stringFormatter, dateFormatter }) => {
  const readonlyNodes = getNodeList(he.root).filter((node) => {
    return node.options.readOnly;
  });

  // Always return an empty validation error array for readonly values
  for (const [format, validator] of Object.entries(he.draft.validateFormat)) {
    he.draft.validateFormat[format] = (node, value) => {
      if (node.schema.options.readOnly) {
        return [];
      }
      return validator(node, value);
    };
  }

  // setValue always revalidates the parent object. Loosen readonly constraints first
  // so sibling selects/enums with null or stale values do not surface errors.
  for (const node of readonlyNodes) {
    relaxReadonlySchemaConstraints(node.schema, getData(node));
  }

  for (const node of readonlyNodes) {
    if (isStringNode(node)) {
      // Use string type to prevent json schema validation
      // e.g. formatting number with postfix as string
      node.schema.type = ['string', 'null'];
      if (node.schema.format === 'date') {
        const formattedValue = dateFormatter(node);
        if (formattedValue !== undefined) {
          he.setValue(node.pointer, formattedValue);
        }
      } else {
        const formattedValue = stringFormatter(node);
        if (formattedValue !== undefined) {
          he.setValue(node.pointer, formattedValue);
        }
      }
    } else if (isNumberNode(node)) {
      // Use string type to prevent json schema validation
      // e.g. formatting number with postfix as string
      node.schema.type = ['string', 'null'];
      const formattedValue = numberFormatter(node);
      if (formattedValue !== undefined) {
        he.setValue(node.pointer, formattedValue);
      }
    }
  }

  // Drop residual errors on display-only nodes after parent revalidation from setValue.
  for (const node of getNodeList(he.root)) {
    if (node.options.readOnly) {
      node.errors = [];
    }
  }

  return {
    id: 'readonly-formatting-plugin',
    onEvent() {
      return undefined;
    },
  };
};
